import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import React, { useContext, useEffect } from "react";
import { grey, red } from "@mui/material/colors";
import MedicationIcon from "@mui/icons-material/Medication";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { NotificationType } from "../../constants/global";
import { useMutationDeleteCart } from "../../queries/product/hooks/useMutationDeleteCart";
import { CART } from "../../queries/product/productTypeDefs";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { UserContext } from "../../hooks/userContext";
import moment from "moment";
import { formatPrice, getSealedDisplay, getPackTypeDisplay, getSealDetailsDisplay } from "../../components/utils/utils";
import axios from "axios";

export const CHECKOUT_TABLE_TEMPLATE =
  "minmax(220px, 2.2fr) minmax(132px, 1.15fr) minmax(100px, 0.9fr) minmax(88px, 0.75fr) minmax(148px, 1.25fr) minmax(112px, 0.95fr) minmax(80px, 0.6fr) minmax(88px, 0.7fr) minmax(92px, 0.7fr) 52px";

export const CHECKOUT_TABLE_MIN_WIDTH = 1240;

export const checkoutTableRowSx = {
  display: "grid",
  gridTemplateColumns: CHECKOUT_TABLE_TEMPLATE,
  alignItems: "stretch",
  borderBottom: "1px solid #e2e8f0",
  "& > .checkout-cell": {
    borderRight: "1px solid #e2e8f0",
    px: 1.5,
    py: 1.25,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  "& > .checkout-cell:last-of-type": {
    borderRight: "none",
    justifyContent: "center",
    px: 0.5,
  },
};

const cellValueSx = {
  fontSize: 14,
  lineHeight: 1.4,
  color: "#1e293b",
  wordBreak: "break-word" as const,
  overflowWrap: "anywhere" as const,
};

interface CheckoutItemProps {
  cartItem: any;
  cartGroupData: any;
  setCartGroupData: any;
  shippingRatesLoading: boolean;
}
export const CheckoutItem: React.FunctionComponent<CheckoutItemProps> = ({
  cartItem,
  cartGroupData,
  setCartGroupData,
  shippingRatesLoading
}) => {
  const isNonSealed = cartItem?.item?.packageCondition !== 1;
  const isPartialPackType = cartItem?.item?.type !== 1;
  const alertBadgeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: red[400],
    color: '#fff',
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1,
    ml: 0.75,
    boxShadow: `0 0 0 1px ${red[400]}`
  };
  const checkoutFieldDetailFontSize = '12px';

  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const [deleteCart, { loading }] = useMutationDeleteCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +id } },
          orderBy: { createdAt: 'asc' }
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);

  useEffect(() => {
    if (!cartItem) return;
    const ndc = cartItem.item.ndc;
    const formattedNdc = ndc.replace(/-/g, '');
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`;
    axios.get(imageUrlApi)
      .then((response) => {
        setImageUrl(response.data.imageUrl);
      })
      .catch((error) => {
        console.error("Error fetching drug image:", error);
      })

  }, [cartItem]);

  const deleteCartItem = (item: any) => {
    const newCartGroupData = [...cartGroupData];

    cartGroupData.forEach((cartGroupDataItem: any, index: number) => {
      if (cartGroupDataItem.groupItem.length === 1) {
        const newGroupItem = [...cartGroupDataItem.groupItem];
        const newCartGroupItemIndex = newGroupItem.findIndex((newGroupIndexItem: any) => newGroupIndexItem.id === item.id);

        if (newCartGroupItemIndex !== -1) {
          newCartGroupData.splice(index, 1);
        }
      } else {
        const newGroupItem = [...cartGroupDataItem.groupItem];
        const newCartGroupItemIndex = newGroupItem.findIndex((newGroupIndexItem: any) => newGroupIndexItem.id === item.id);
        if (newCartGroupItemIndex !== -1) {
          newGroupItem.splice(newCartGroupItemIndex, 1);
          newCartGroupData[index] = {
            ...newCartGroupData[index],
            groupItem: newGroupItem
          }
        }
      }
    });

    setCartGroupData(newCartGroupData);

    if (!loading) {
      deleteCart({ variables: { where: { id: item.id } } }).catch((error: any) => {
        addToast({ type: NotificationType.Error, title: error.message });
      });
    }
  }

  return (
    <Box sx={checkoutTableRowSx}>
      <Box className="checkout-cell">
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0, width: "100%" }}>
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt=""
              sx={{ width: 40, height: 40, borderRadius: 1, objectFit: "cover", flexShrink: 0, border: "1px solid #e2e8f0" }}
            />
          ) : (
            <MedicationIcon sx={{ color: grey[500], flexShrink: 0, mt: 0.25 }} />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.45, color: "#1e293b", wordBreak: "break-word" }}>
              {cartItem.item.drugName}
              {cartItem.item.isFrozen && (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.4,
                    ml: 0.75,
                    px: 0.9,
                    py: 0.15,
                    borderRadius: "999px",
                    bgcolor: "#e0f2fe",
                    color: "#0369a1",
                    border: "1px solid #bae6fd",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    verticalAlign: "middle",
                    whiteSpace: "nowrap",
                  }}
                >
                  <AcUnitIcon sx={{ fontSize: 13 }} />
                  Refrigerated
                </Box>
              )}
            </Box>
            {cartItem.item.labelerName && (
              <Box sx={{ mt: 0.25, fontSize: 12, lineHeight: 1.35, color: "#64748b", wordBreak: "break-word" }}>
                {cartItem.item.labelerName}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className="checkout-cell">
        <Box sx={cellValueSx}>{cartItem.item.ndc}</Box>
      </Box>
      <Box className="checkout-cell">
        <Box sx={cellValueSx}>{cartItem.item.strength}</Box>
      </Box>
      <Box className="checkout-cell">
        <Box sx={cellValueSx}>{moment(cartItem.item.expDate).format("MM/YYYY")}</Box>
      </Box>
      <Box className="checkout-cell">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
          <Box sx={{ ...cellValueSx, display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
            {getSealedDisplay(cartItem.item.packageCondition, cartItem.item.type)}
            {isNonSealed && (
              <Tooltip title="Non-Sealed Product" arrow placement="top">
                <Box component="span" sx={alertBadgeSx}>!</Box>
              </Tooltip>
            )}
          </Box>
          {cartItem.item.sealDetails && (
            <Box sx={{ fontSize: checkoutFieldDetailFontSize, color: "text.secondary", mt: 0.5, wordBreak: "break-word" }}>
              {getSealDetailsDisplay(cartItem.item.sealDetails)}
            </Box>
          )}
        </Box>
      </Box>
      <Box className="checkout-cell">
        <Box sx={{ ...cellValueSx, display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
          {getPackTypeDisplay(cartItem.item.type, cartItem.item.packaging)}
          {isPartialPackType && (
            <Tooltip title="Partial Product" arrow placement="top">
              <Box component="span" sx={alertBadgeSx}>!</Box>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box className="checkout-cell">
        <Box sx={cellValueSx}>{cartItem.quantity}</Box>
      </Box>
      <Box className="checkout-cell">
        <Box sx={cellValueSx}>${formatPrice(cartItem.item.unitPrice)}</Box>
      </Box>
      <Box className="checkout-cell">
        <Box sx={{ ...cellValueSx, fontWeight: 600 }}>${formatPrice(cartItem.item.price)}</Box>
      </Box>
      <Box className="checkout-cell">
        <IconButton
          aria-label="Remove item"
          size="small"
          disabled={shippingRatesLoading}
          onClick={() => deleteCartItem(cartItem)}
          sx={{ color: shippingRatesLoading ? grey[500] : red[500] }}
        >
          <DeleteOutlineOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}